// src/components/dashboard/Offers.jsx
import React, { useState, useEffect } from 'react';
import { FaCrown, FaGift, FaTag, FaStar, FaTimes, FaFire, FaPercent, FaGem, FaChevronRight, FaCheck } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';

const Offers = ({ onBack }) => {
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Trigger entrance animation
    setIsVisible(true);
  }, []);

  const offerCards = [
    {
      id: 1,
      title: "Premium Membership",
      description: "Unlock all premium features with our best value plan",
      originalPrice: "₹5999",
      discountedPrice: "₹2999",
      discount: "50% OFF",
      period: "6-month plan",
      icon: <FaCrown />,
      gradient: "from-purple-600 to-indigo-700",
      badge: "Most Popular",
      features: ["Ad-free experience", "Priority support", "Unlimited matches", "Advanced filters"],
      highlight: true
    },
    {
      id: 2,
      title: "Contact Reveal Pack",
      description: "Reveal contacts and connect directly with your matches",
      originalPrice: "₹1999",
      discountedPrice: "₹999",
      discount: "50% OFF",
      period: "10 contacts",
      icon: <FaGem />,
      gradient: "from-emerald-600 to-teal-700",
      badge: "Limited Time",
      features: ["Reveal 10 contacts", "Unlimited messages", "Read receipts", "Profile highlights"],
      highlight: false
    },
    {
      id: 3,
      title: "Featured Profile Boost",
      description: "Get noticed by more potential matches in your area",
      originalPrice: "₹999",
      discountedPrice: "₹499",
      discount: "50% OFF",
      period: "7 days",
      icon: <FaFire />,
      gradient: "from-amber-600 to-orange-700",
      badge: "Trending",
      features: ["10x more visibility", "Top of search results", "Priority in suggestions", "Boost badge"],
      highlight: false
    },
    {
      id: 4,
      title: "Super Likes Pack",
      description: "Stand out and show special interest in your favorites",
      originalPrice: "₹799",
      discountedPrice: "₹399",
      discount: "50% OFF",
      period: "20 Super Likes",
      icon: <FaStar />,
      gradient: "from-rose-600 to-pink-700",
      badge: "New",
      features: ["Get noticed instantly", "Double match chance", "Priority placement", "Special notification"],
      highlight: false
    },
    {
      id: 5,
      title: "Travel Mode",
      description: "Connect with people in other locations before you arrive",
      originalPrice: "₹1499",
      discountedPrice: "₹749",
      discount: "50% OFF",
      period: "30 days",
      icon: <FaGift />,
      gradient: "from-cyan-600 to-blue-700",
      badge: "Exclusive",
      features: ["Change location", "Global matching", "Local insights", "Travel badge"],
      highlight: false
    },
    {
      id: 6,
      title: "Incognito Mode",
      description: "Browse profiles privately without appearing in others' views",
      originalPrice: "₹1299",
      discountedPrice: "₹649",
      discount: "50% OFF",
      period: "1 month",
      icon: <FaTag />,
      gradient: "from-violet-600 to-purple-700",
      badge: "Privacy",
      features: ["Browse anonymously", "Control visibility", "Selective appearing", "Stealth mode"],
      highlight: false
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const cardVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 12
      }
    },
    hover: {
      y: -8,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 15
      }
    }
  };

  const modalVariants = {
    hidden: { opacity: 0, scale: 0.9 },
    visible: { 
      opacity: 1, 
      scale: 1,
      transition: {
        type: "spring",
        stiffness: 200,
        damping: 25
      }
    }
  };

  const handleOfferSelect = (offer) => {
    setSelectedOffer(offer);
  };

  const closeModal = () => {
    setSelectedOffer(null);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div 
          className="flex flex-col md:flex-row justify-between items-start md:items-center mb-10 md:mb-12"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: isVisible ? 1 : 0, y: isVisible ? 0 : -20 }}
          transition={{ duration: 0.6 }}
        >
          <div className="flex items-center mb-6 md:mb-0">
            <div className="relative">
              <FaCrown className="text-5xl md:text-6xl text-amber-500 mr-4" />
              <FaPercent className="absolute -top-2 -right-2 text-xl bg-amber-500 text-white rounded-full p-1" />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900 font-serif">Exclusive Offers</h1>
              <p className="text-gray-600 text-lg md:text-xl mt-1">Limited-time discounts & premium features</p>
            </div>
          </div>

          <button
            onClick={onBack}
            className="flex items-center gap-3 px-6 py-3 bg-white hover:bg-gray-50 text-gray-700 font-semibold rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl border border-gray-200 group"
            aria-label="Close offers"
          >
            <FaTimes className="text-lg group-hover:rotate-90 transition-transform duration-300" />
            <span className="text-base">Close Offers</span>
          </button>
        </motion.div>

        {/* Timer Banner */}
        <motion.div 
          className="bg-gradient-to-r from-purple-700 to-indigo-800 text-white rounded-2xl p-6 mb-10 shadow-xl"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: isVisible ? 1 : 0, scale: isVisible ? 1 : 0.95 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <div className="flex flex-col md:flex-row items-center justify-between">
            <div className="flex items-center mb-4 md:mb-0">
              <div className="bg-white text-purple-700 rounded-full p-3 mr-4">
                <FaFire className="text-2xl" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">Flash Sale Ending Soon!</h2>
                <p className="text-purple-100">All offers expire in <span className="font-bold">24 hours</span></p>
              </div>
            </div>
            
            <div className="flex items-center space-x-6">
              <div className="text-center">
                <div className="bg-black/20 rounded-lg px-4 py-2">
                  <span className="text-3xl font-bold">12</span>
                  <p className="text-sm text-purple-200">Hours</p>
                </div>
              </div>
              <div className="text-center">
                <div className="bg-black/20 rounded-lg px-4 py-2">
                  <span className="text-3xl font-bold">45</span>
                  <p className="text-sm text-purple-200">Minutes</p>
                </div>
              </div>
              <div className="text-center">
                <div className="bg-black/20 rounded-lg px-4 py-2">
                  <span className="text-3xl font-bold">22</span>
                  <p className="text-sm text-purple-200">Seconds</p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Offers Grid */}
        <motion.div 
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          variants={containerVariants}
          initial="hidden"
          animate={isVisible ? "visible" : "hidden"}
        >
          {offerCards.map((offer) => (
            <motion.div
              key={offer.id}
              variants={cardVariants}
              whileHover="hover"
              className={`relative ${offer.highlight ? 'lg:col-span-2 lg:row-span-2' : ''}`}
            >
              <div 
                className={`bg-gradient-to-br ${offer.gradient} text-white rounded-2xl p-7 shadow-2xl h-full flex flex-col transform transition-all duration-300 border border-white/20 overflow-hidden relative`}
              >
                {/* Badge */}
                <div className="absolute top-5 right-5">
                  <span className="bg-white/30 backdrop-blur-sm text-white font-bold text-sm px-4 py-1.5 rounded-full">
                    {offer.badge}
                  </span>
                </div>
                
                {/* Ribbon for highlighted offer */}
                {offer.highlight && (
                  <div className="absolute top-0 left-0 bg-gradient-to-r from-amber-400 to-amber-500 text-gray-900 font-bold text-sm px-8 py-2 rounded-br-xl shadow-lg">
                    BEST VALUE
                  </div>
                )}

                {/* Offer Icon */}
                <div className="mb-6">
                  <div className="bg-white/20 w-16 h-16 rounded-2xl flex items-center justify-center backdrop-blur-sm">
                    <div className="text-3xl">
                      {offer.icon}
                    </div>
                  </div>
                </div>

                {/* Offer Title & Description */}
                <h3 className="text-2xl font-bold mb-3 font-serif">{offer.title}</h3>
                <p className="text-white/90 mb-6 text-base leading-relaxed">{offer.description}</p>
                
                {/* Features List */}
                <div className="mb-6 flex-grow">
                  <ul className="space-y-2">
                    {offer.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center text-sm">
                        <FaCheck className="text-white/80 mr-3 text-xs" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Price Section */}
                <div className="mt-auto">
                  <div className="flex items-baseline mb-2">
                    <span className="text-4xl font-bold">{offer.discountedPrice}</span>
                    <span className="line-through ml-3 text-xl opacity-70">{offer.originalPrice}</span>
                    <span className="ml-auto bg-white/30 text-white font-bold text-sm px-3 py-1 rounded-full">
                      {offer.discount}
                    </span>
                  </div>
                  <p className="text-white/80 text-sm mb-6">{offer.period}</p>
                  
                  <button 
                    onClick={() => handleOfferSelect(offer)}
                    className="w-full bg-white hover:bg-gray-100 text-gray-900 font-bold py-4 rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl flex items-center justify-center group"
                  >
                    <span>Select Offer</span>
                    <FaChevronRight className="ml-2 group-hover:translate-x-1 transition-transform" />
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Footer Note */}
        <motion.div 
          className="mt-16 text-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: isVisible ? 1 : 0 }}
          transition={{ duration: 0.6, delay: 0.8 }}
        >
          <div className="inline-flex items-center bg-gradient-to-r from-gray-100 to-white p-6 rounded-2xl shadow-lg border border-gray-200">
            <div className="bg-emerald-100 text-emerald-700 rounded-full p-3 mr-4">
              <FaGift className="text-2xl" />
            </div>
            <div className="text-left">
              <p className="text-gray-800 text-lg font-medium">All offers include a 7-day money-back guarantee</p>
              <p className="text-gray-600 text-sm">Subscribe risk-free. Cancel anytime for a full refund.</p>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Offer Detail Modal */}
      <AnimatePresence>
        {selectedOffer && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50 backdrop-blur-sm">
            <motion.div 
              className="bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
              variants={modalVariants}
              initial="hidden"
              animate="visible"
              exit="hidden"
            >
              <div className={`bg-gradient-to-br ${selectedOffer.gradient} text-white p-8 rounded-t-3xl`}>
                <div className="flex justify-between items-start">
                  <div>
                    <div className="flex items-center mb-4">
                      <div className="bg-white/20 w-14 h-14 rounded-2xl flex items-center justify-center backdrop-blur-sm mr-4">
                        <div className="text-2xl">
                          {selectedOffer.icon}
                        </div>
                      </div>
                      <h2 className="text-3xl font-bold font-serif">{selectedOffer.title}</h2>
                    </div>
                    <p className="text-white/90 text-lg">{selectedOffer.description}</p>
                  </div>
                  <button 
                    onClick={closeModal}
                    className="bg-white/20 hover:bg-white/30 text-white rounded-full p-3 transition-colors"
                  >
                    <FaTimes className="text-xl" />
                  </button>
                </div>
              </div>
              
              <div className="p-8">
                <div className="mb-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">What's Included</h3>
                  <ul className="space-y-3">
                    {selectedOffer.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center text-gray-700">
                        <div className={`bg-gradient-to-br ${selectedOffer.gradient} text-white w-8 h-8 rounded-full flex items-center justify-center mr-4`}>
                          <FaCheck className="text-sm" />
                        </div>
                        <span className="text-lg">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                
                <div className="bg-gray-50 rounded-2xl p-6 mb-8">
                  <div className="flex flex-col md:flex-row justify-between items-center">
                    <div>
                      <div className="flex items-baseline mb-2">
                        <span className="text-5xl font-bold text-gray-900">{selectedOffer.discountedPrice}</span>
                        <span className="line-through ml-4 text-2xl text-gray-500">{selectedOffer.originalPrice}</span>
                      </div>
                      <p className="text-gray-600">You save <span className="font-bold text-emerald-600">{selectedOffer.discount}</span> with this offer</p>
                      <p className="text-gray-600 mt-1">Valid for {selectedOffer.period}</p>
                    </div>
                    
                    <div className="mt-6 md:mt-0">
                      <button className="bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white font-bold py-4 px-10 rounded-xl text-lg shadow-lg hover:shadow-xl transition-all duration-300">
                        Get This Offer Now
                      </button>
                    </div>
                  </div>
                </div>
                
                <div className="text-center">
                  <button 
                    onClick={closeModal}
                    className="text-gray-600 hover:text-gray-800 font-medium transition-colors"
                  >
                    ← Back to all offers
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Offers;